import React from 'react';
import { Box, Grid, Button, Typography, Stack } from '@mui/material';
import { DemoReelCard, DemoReelCardProps } from './Cards/DemoReelCard';
import { DemoReelEmptyCard } from './ReelEmptyStates';
import { IProject } from '../../types/global/helper';
import { ThemeStyle } from '../../module/ThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';

interface ProjectListProps
    extends Pick<
        DemoReelCardProps,
        'isEditMode' | 'onDelete' | 'onClickPlay' | 'onExpandToPlayer' | 'onInlinePlaybackStart'
    > {
    projects?: IProject[];
    themeStyle: ThemeStyle;
    isEditMode?: boolean;
    onAddNewItem?: () => void;
    title?: string;
    disableAddButton?: boolean;
    /** Smaller uppercase section label (technical profile layout) */
    compactSectionTitle?: boolean;
    /** Flush label + full-width empty card to align with primary title card (content creator). */
    alignWithTitleCard?: boolean;
    /** Artist studio: muted uppercase wide tracking section label (matches technical card chrome). */
    artistStudioTitle?: boolean;
    /** Media tab: parent supplies section title + subtitle; hide this grid’s label. */
    omitSectionTitle?: boolean;
    /** Remove grid inset so card edges align with section/container edges. */
    flushLayout?: boolean;
    /** Not the profile owner: neutral empty state (no feature blurbs). */
    visitorView?: boolean;
    /** Placeholder style when no projects are selected. */
    emptyStateVariant?: 'profile' | 'campaignVideo';
}

const DemoReelGrid: React.FC<ProjectListProps> = ({
    projects,
    themeStyle,
    isEditMode,
    onAddNewItem,
    title,
    disableAddButton,
    compactSectionTitle,
    alignWithTitleCard,
    artistStudioTitle,
    omitSectionTitle,
    flushLayout,
    visitorView,
    emptyStateVariant,
    ...props
}) => {
    if (!projects) {
        return <div />;
    }

    const sectionTitle = (
        <Typography
            sx={{
                color: artistStudioTitle ? 'rgba(255,255,255,0.5)' : theme[themeStyle].highlight,
                fontFamily: FONT_ENUM.MAIN_BOLD,
                fontWeight: compactSectionTitle || artistStudioTitle ? 700 : undefined,
                fontSize: artistStudioTitle ? 14 : compactSectionTitle ? 10 : 16,
                letterSpacing: artistStudioTitle ? '0.22em' : compactSectionTitle ? '0.14em' : undefined,
                textTransform: artistStudioTitle || compactSectionTitle ? 'uppercase' : 'none',
                px: alignWithTitleCard || artistStudioTitle ? 0 : compactSectionTitle ? 1 : 2,
                mb: 0,
                width: '100%',
            }}>
            {title ?? 'Demo Reel'}
        </Typography>
    );

    const addButton =
        isEditMode && !disableAddButton && projects.length > 0 ? (
            <Button
                onClick={onAddNewItem}
                sx={{
                    mt: 1,
                    width: '100%',
                    height: 44,
                    backgroundColor: theme[themeStyle].highlight,
                    color: theme[themeStyle].black,
                    borderRadius: '10px',
                    textTransform: 'none',
                    fontFamily: FONT_ENUM.MAIN_BOLD,
                    '&:hover': { backgroundColor: theme[themeStyle].primary_orange },
                }}>
                Add Demo Reel
            </Button>
        ) : null;

    if (artistStudioTitle) {
        return (
            <Stack spacing={2} sx={{ width: '100%', maxWidth: '100%', mt: 0, alignItems: 'stretch' }}>
                <Box sx={{ width: '100%' }}>{sectionTitle}</Box>
                {projects.map((project, index) => (
                    <DemoReelCard
                        key={index}
                        project={project}
                        isEditMode={isEditMode}
                        variant="artistFeatured"
                        {...props}
                    />
                ))}
                {projects.length === 0 && (
                    <DemoReelEmptyCard
                        themeStyle={themeStyle}
                        isEditMode={isEditMode}
                        onUpload={!disableAddButton ? onAddNewItem : undefined}
                        fullWidth
                        featuredHero
                        visitorView={visitorView}
                        emptyStateVariant={emptyStateVariant}
                    />
                )}
                {addButton}
            </Stack>
        );
    }

    return (
        <Grid
            container
            spacing={flushLayout ? 1.5 : 2}
            mt={omitSectionTitle ? 0 : 2}
            sx={{ width: '100%', maxWidth: '100%', mx: 0 }}>
            {!omitSectionTitle && sectionTitle}
            {projects.map((project, index) => (
                <Grid item xs={12} key={index} sx={{ px: flushLayout ? '0 !important' : undefined }}>
                    <DemoReelCard project={project} isEditMode={isEditMode} {...props} />
                </Grid>
            ))}
            {projects.length === 0 && (
                <Grid
                    item
                    xs={12}
                    sx={{
                        width: '100%',
                        maxWidth: '100%',
                        minWidth: 0,
                        px: flushLayout ? '0 !important' : undefined,
                    }}>
                    <DemoReelEmptyCard
                        themeStyle={themeStyle}
                        isEditMode={isEditMode}
                        onUpload={!disableAddButton ? onAddNewItem : undefined}
                        fullWidth={alignWithTitleCard}
                        visitorView={visitorView}
                        emptyStateVariant={emptyStateVariant}
                    />
                </Grid>
            )}
            {addButton}
        </Grid>
    );
};

export { DemoReelGrid };
